import { PlayerAvatar } from "@client/components/player-avatar";
import { cardLabel } from "../cards/card-art";
import { useEffect, useRef } from "react";
import type { GameCommand, PlayerProjection } from "@saboteur/contracts";
import { getCardDefinition } from "@saboteur/contracts/cards";
import { CardFace, ToolIcon } from "../cards/card-face";
type HandCard = PlayerProjection["self"]["hand"][number];
export interface TurnControlsProps {
  selectedCard?: HandCard | undefined;
  legalCommands: PlayerProjection["self"]["legalCommands"];
  pendingCommand?: GameCommand | undefined;
  error?: string | undefined;
  participants?: PlayerProjection["public"]["participants"];
  selfParticipantId?: string;
  busy?: boolean;
  disabled?: boolean;
  rotation?: 0 | 180;
  onPrepare: (command: GameCommand) => void;
  onConfirm: () => void;
  onCancel: () => void;
  onRotate?: () => void;
}
function confirmationLabel(
  command: GameCommand,
  participants: PlayerProjection["public"]["participants"] = [],
  selfParticipantId?: string,
) {
  switch (command.type) {
    case "turn.play-path":
      return "Place tunnel";
    case "turn.rockfall":
      return "Remove tunnel";
    case "turn.inspect-goal":
      return "Inspect goal privately";
    case "turn.discard":
      return "Discard card";
    case "turn.break-tool":
    case "turn.repair-tool":
      return `${command.type === "turn.break-tool" ? "Break" : "Repair"} ${command.targetParticipantId === selfParticipantId ? "your" : `${participants.find((p) => p.participantId === command.targetParticipantId)?.displayName ?? "player"}’s`} ${command.tool.toLowerCase()}`;
    default:
      return "Confirm";
  }
}
export function TurnControls({
  selectedCard,
  legalCommands,
  pendingCommand,
  participants = [],
  selfParticipantId,
  busy = false,
  disabled = false,
  error,
  rotation = 0,
  onPrepare,
  onConfirm,
  onCancel,
  onRotate,
}: TurnControlsProps) {
  const confirmButton = useRef<HTMLButtonElement>(null);
  const inspection = useRef<HTMLDialogElement>(null);
  const isToolCard = selectedCard?.kind === "BREAK_TOOL" || selectedCard?.kind === "REPAIR_TOOL";
  const isBreak = selectedCard?.kind === "BREAK_TOOL";
  const targets = legalCommands.targetsByCard.find(
    (target) => target.cardId === selectedCard?.cardId,
  );
  const targetPlayers = [...participants].sort(
    (a, b) =>
      Number(b.participantId === selfParticipantId) - Number(a.participantId === selfParticipantId),
  );
  const hasEligibleTarget = targetPlayers.some((player) =>
    targets?.participantIds.includes(player.participantId),
  );
  const toolConfirmation =
    pendingCommand?.type === "turn.break-tool" || pendingCommand?.type === "turn.repair-tool";
  useEffect(() => {
    if (pendingCommand) confirmButton.current?.focus();
  }, [pendingCommand]);
  if (!selectedCard && !error) return null;
  return (
    <section
      className={`turn-controls${isToolCard ? " turn-controls--tool" : ""}`}
      aria-label="Turn controls"
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      {selectedCard ? (
        <>
          <button
            type="button"
            className="selected-card-preview"
            aria-label="Inspect selected card"
            onClick={() => inspection.current?.showModal()}
          >
            <CardFace faceId={selectedCard.faceId} rotation={rotation} />
          </button>
          <dialog ref={inspection} className="card-inspection">
            <form method="dialog">
              <button className="dialog-close" aria-label="Close card inspection">
                ×
              </button>
            </form>
            <h2>{cardLabel(selectedCard.faceId)}</h2>
            <CardFace faceId={selectedCard.faceId} rotation={rotation} />
            <p>{getCardDefinition(selectedCard.faceId).description}</p>
          </dialog>
          <div className="selected-card-copy">
            <strong>
              {selectedCard.kind === "PATH" ? "Tunnel card" : cardLabel(selectedCard.faceId)}
            </strong>
            <p>
              {selectedCard.kind === "BREAK_TOOL"
                ? "Choose a player to block."
                : selectedCard.kind === "REPAIR_TOOL"
                  ? "Choose a tool to repair."
                  : selectedCard.kind === "MAP"
                    ? "Choose a destination to peek at."
                    : selectedCard.kind === "ROCKFALL"
                      ? "Choose a tunnel to remove."
                      : cardLabel(selectedCard.faceId)}
            </p>
          </div>
          {isToolCard && !pendingCommand && !disabled ? (
            <div
              className={`tool-picker ${isBreak ? "tool-picker--break" : "tool-picker--repair"}`}
            >
              <div
                className="tool-picker__targets"
                role="group"
                aria-label="Choose a player and tool"
              >
                {targetPlayers.flatMap((player) =>
                  (targets?.tools ?? []).map((tool) => {
                    const broken = player.brokenTools.includes(tool);
                    const eligible =
                      targets?.participantIds.includes(player.participantId) &&
                      (isBreak ? !broken : broken);
                    if (!isBreak && !eligible) return null;
                    return (
                      <button
                        key={`${player.participantId}-${tool}`}
                        type="button"
                        disabled={busy || !eligible}
                        aria-label={`${isBreak ? "Break" : "Repair"} ${player.participantId === selfParticipantId ? "your" : `${player.displayName}’s`} ${tool.toLowerCase()}`}
                        onClick={() =>
                          onPrepare({
                            type: isBreak ? "turn.break-tool" : "turn.repair-tool",
                            cardId: selectedCard.cardId,
                            targetParticipantId: player.participantId,
                            tool,
                          })
                        }
                      >
                        <PlayerAvatar seat={player.avatarIndex ?? player.seatIndex} />
                        <ToolIcon tool={tool} broken={broken} />
                        <span>
                          <strong>
                            {player.participantId === selfParticipantId
                              ? "You"
                              : player.displayName}
                          </strong>
                          <small>
                            {eligible ? tool.toLowerCase() : broken ? "Broken" : "Working"}
                          </small>
                        </span>
                      </button>
                    );
                  }),
                )}
              </div>
              {!hasEligibleTarget ? (
                <p className="turn-hint">
                  No tools need this card right now. Choose another card or discard it.
                </p>
              ) : null}
            </div>
          ) : null}
          {toolConfirmation ? (
            <p className="tool-confirmation" role="status">
              {confirmationLabel(pendingCommand, participants, selfParticipantId)}?{" "}
              {pendingCommand.type === "turn.break-tool"
                ? pendingCommand.targetParticipantId === selfParticipantId
                  ? "You won’t be able to place tunnels until it’s repaired."
                  : "They’ll need a repair before placing tunnels."
                : "Only this tool will be repaired."}
            </p>
          ) : null}
          <div className="action-buttons">
            {selectedCard.kind === "PATH" && !pendingCommand && onRotate ? (
              <button
                type="button"
                className="button-secondary rotate-card-button"
                aria-label="Rotate tunnel card"
                title="Rotate tunnel card"
                disabled={busy || disabled}
                onClick={onRotate}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path
                    d="M19 8a8 8 0 1 0 1 8M19 3v5h-5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
            {pendingCommand ? (
              <>
                <button
                  className="button-secondary"
                  type="button"
                  disabled={busy}
                  onClick={onCancel}
                >
                  Cancel
                </button>
                <button
                  ref={confirmButton}
                  type="button"
                  disabled={busy || disabled}
                  onClick={onConfirm}
                >
                  {busy
                    ? "Sending…"
                    : confirmationLabel(pendingCommand, participants, selfParticipantId)}
                </button>
              </>
            ) : legalCommands.discardableCardIds.includes(selectedCard.cardId) ? (
              <button
                className="button-secondary"
                disabled={busy || disabled}
                type="button"
                onClick={() => onPrepare({ type: "turn.discard", cardId: selectedCard.cardId })}
              >
                Discard card
              </button>
            ) : null}
          </div>
        </>
      ) : null}
      {error ? (
        <p className="action-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
